import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ActivityIcon, AlertTriangleIcon, BellIcon, FileCheckIcon, MapPinnedIcon, SearchIcon, TruckIcon } from 'lucide-react';
type Trip = {
  id: string;
  route: string;
  carrier: string;
  unit: string;
  state: 'En tránsito' | 'En carga' | 'Entregado' | 'Desvío';
  eta: string;
  docs: 'OK' | 'Pendiente';
};
const TRIPS: Trip[] = [{
  id: 'VJ-10428',
  route: 'Pergamino → Rosario',
  carrier: 'Transportista A',
  unit: 'Chasis + acoplado',
  state: 'En tránsito',
  eta: 'Hoy 16:40',
  docs: 'OK'
}, {
  id: 'VJ-10431',
  route: 'Pergamino → CABA',
  carrier: 'Red 3PL',
  unit: 'Batea 35 TN',
  state: 'En carga',
  eta: 'Hoy 19:10',
  docs: 'Pendiente'
}, {
  id: 'VJ-10433',
  route: 'Córdoba → Bahía Blanca',
  carrier: 'Transportista B',
  unit: '28 pallets',
  state: 'Desvío',
  eta: 'Mañana 09:20',
  docs: 'OK'
}, {
  id: 'VJ-10437',
  route: 'Rosario → Neuquén',
  carrier: 'Flota propia',
  unit: 'Chasis + acoplado',
  state: 'En tránsito',
  eta: 'Mañana 14:00',
  docs: 'OK'
}, {
  id: 'VJ-10440',
  route: 'CABA → Pergamino',
  carrier: 'Transportista C',
  unit: 'Liviano 500 kg',
  state: 'Entregado',
  eta: 'Ayer 18:05',
  docs: 'OK'
}];
const STATE_STYLES: Record<Trip['state'], string> = {
  'En tránsito': 'bg-brand-500/15 text-brand-200 border-brand-400/30',
  'En carga': 'bg-white/10 text-mist-200 border-white/20',
  Entregado: 'bg-brand-700/40 text-brand-100 border-brand-500/30',
  Desvío: 'bg-accent-500/15 text-accent-300 border-accent-400/40'
};
const TABS = ['Operación', 'Transportistas', 'Documentación', 'SLA'] as const;
export function ControlTowerDashboard() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Operación');
  return <div className="overflow-hidden border border-white/10 bg-graphite-800 shadow-2xl" data-id="element-117" data-mp="21zv6yp71px3q">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 bg-graphite-900 px-4 py-3" data-id="element-118" data-mp="38ee0fgitf4ty">
        <div className="flex items-center gap-3" data-id="element-119" data-mp="3dcxpvqwmayr2">
          <span className="flex h-7 w-7 items-center justify-center bg-brand-600 font-display text-xs font-bold text-white" data-id="element-120" data-mp="1dbq0aycl8v2i">R</span>
          <span className="font-display text-sm font-semibold text-white" data-id="element-121" data-mp="199w3x29fed85">Control Tower</span>
          <span className="border border-accent-400/40 bg-accent-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-accent-300" data-id="element-122" data-mp="1n9dsphmq1qhi">
            Demo
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-123" data-mp="umt24je2lif0">
          <div className="hidden items-center gap-2 border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-mist-300 sm:flex" data-id="element-124" data-mp="pmonkywq2xrf">
            <SearchIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-125" data-mp="14bujoe09k7v3" />
            <span data-id="element-126" data-mp="zkunjayyxttn">Buscar viaje, patente o transportista</span>
          </div>
          <span className="relative inline-flex h-8 w-8 items-center justify-center border border-white/10 bg-white/5 text-mist-200" data-id="element-127" data-mp="ziro60bxeoc9">
            <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="6wgm0l8o8fdc" />
            <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-accent-400" data-id="element-129" data-mp="6u2w0qsultcn" />
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 bg-graphite-800 px-2" data-id="element-130" data-mp="2nbameo2eh06l">
        {TABS.map(t => <button key={t} type="button" onClick={() => setTab(t)} className={`border-b-2 px-4 py-3 text-xs font-semibold transition-colors duration-150 ${tab === t ? 'border-accent-400 text-white' : 'border-transparent text-mist-300 hover:text-white'}`} data-id="element-131" data-mp="29k5hb6g9jfov">
            {t}
          </button>)}
      </div>

      <div className="grid gap-px bg-white/10 lg:grid-cols-[1.55fr_1fr]" data-id="element-132" data-mp="2s8k75qeugno3">
        {/* Panel principal */}
        <div className="bg-graphite-800 p-4 md:p-6" data-id="element-133" data-mp="2x6vhs3cyr27d">
          <div className="grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4" data-id="element-134" data-mp="1gugdklc651jk">
            {[{
            icon: TruckIcon,
            label: 'Viajes activos',
            value: '38'
          }, {
            icon: ActivityIcon,
            label: 'En tránsito',
            value: '21'
          }, {
            icon: FileCheckIcon,
            label: 'Documentación OK',
            value: '96%'
          }, {
            icon: AlertTriangleIcon,
            label: 'Desvíos abiertos',
            value: '2'
          }].map(kpi => <div key={kpi.label} className="bg-graphite-800 p-4" data-id="element-135" data-mp="1rdwqammjkpu6">
                <kpi.icon className="h-4 w-4 text-brand-300" aria-hidden="true" data-id="element-136" data-mp="1nc1qjmk9ekii" />
                <p className="mt-3 font-display text-2xl font-bold tabular-nums text-white" data-id="element-137" data-mp="2k1sj3g8zpxlo">{kpi.value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-mist-300" data-id="element-138" data-mp="2f2xqxnp4uwow">{kpi.label}</p>
              </div>)}
          </div>

          <table className="mt-6 w-full text-left text-xs" data-id="element-139" data-mp="2azjbay53wibz">
            <caption className="sr-only" data-id="element-140" data-mp="262j76srthpni">Listado de viajes en seguimiento</caption>
            <thead data-id="element-141" data-mp="zlnwjqi46bla">
              <tr className="text-[10px] uppercase tracking-[0.12em] text-mist-300" data-id="element-142" data-mp="zm7db3w12txq">
                <th scope="col" className="pb-3 font-semibold" data-id="element-143" data-mp="umbah7z2h8jr">Viaje</th>
                <th scope="col" className="pb-3 font-semibold" data-id="element-144" data-mp="up76gqrctn5k">Ruta</th>
                <th scope="col" className="hidden pb-3 font-semibold md:table-cell" data-id="element-145" data-mp="36br4uf6p1hg6">Transportista</th>
                <th scope="col" className="pb-3 font-semibold" data-id="element-146" data-mp="3b9iz3fut9fnj">Estado</th>
                <th scope="col" className="hidden pb-3 font-semibold sm:table-cell" data-id="element-147" data-mp="3f9tdngwo5ee0">ETA</th>
                <th scope="col" className="pb-3 font-semibold" data-id="element-148" data-mp="3k1urh79hjd65">Doc.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-149" data-mp="3ozmi7xye8t8r">
              {TRIPS.map(trip => <tr key={trip.id} className="text-mist-200" data-id="element-150" data-mp="3typnnjiad1h9">
                  <td className="py-3 font-medium tabular-nums text-white" data-id="element-151" data-mp="2thdvweig56d">{trip.id}</td>
                  <td className="py-3" data-id="element-152" data-mp="2rmqmbfro0z4">{trip.route}</td>
                  <td className="hidden py-3 md:table-cell" data-id="element-153" data-mp="6trnrk5fbfqi">{trip.carrier}</td>
                  <td className="py-3" data-id="element-154" data-mp="24lm6la3u36i5">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-semibold ${STATE_STYLES[trip.state]}`} data-id="element-155" data-mp="24mnj7j0i011z">
                      {trip.state}
                    </span>
                  </td>
                  <td className="hidden py-3 tabular-nums sm:table-cell" data-id="element-156" data-mp="3bz6d7k5j1bfu">{trip.eta}</td>
                  <td className="py-3" data-id="element-157" data-mp="37039c31a9b9g">
                    <span className={trip.docs === 'OK' ? 'text-brand-300' : 'text-accent-300'} data-id="element-158" data-mp="3kyjbrhs44fls">{trip.docs}</span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Panel lateral */}
        <div className="bg-graphite-800 p-4 md:p-6" data-id="element-159" data-mp="3uucz7w7ddtic">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-mist-300" data-id="element-160" data-mp="3pxmg1009qhsp">Cumplimiento de SLA</h3>
          <p className="mt-3 font-display text-4xl font-bold tabular-nums text-white" data-id="element-161" data-mp="8fl94ay0df29">90%</p>
          <p className="mt-1 text-xs text-mist-300" data-id="element-162" data-mp="3ikyrnu4lq5c">objetivo dentro de 72 h — se busca 48 h cuando las condiciones lo permiten</p>
          <div className="mt-4 h-1.5 w-full bg-white/10" data-id="element-163" data-mp="hhkpd1178f45">
            <motion.div className="h-full bg-accent-400" initial={{
            width: 0
          }} whileInView={{
            width: '90%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-164" data-mp="cizn6w2dzld2" />
          </div>

          <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.14em] text-mist-300" data-id="element-165" data-mp="n3v1zz2u954x">Alertas operativas</h3>
          <ul className="mt-3 space-y-3" data-id="element-166" data-mp="r3lvwbnnw1do">
            {[{
            icon: AlertTriangleIcon,
            text: 'Desvío de ruta detectado en VJ-10433 — coordinación notificada.'
          }, {
            icon: FileCheckIcon,
            text: 'Documentación pendiente en VJ-10431 — bloqueo previo a carga.'
          }, {
            icon: MapPinnedIcon,
            text: 'Parada no planificada superior a lo previsto — Call Center post carga.'
          }].map(alert => <li key={alert.text} className="flex gap-3 border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-mist-200" data-id="element-167" data-mp="8ccivseomuhc">
                <alert.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-400" aria-hidden="true" data-id="element-168" data-mp="3vjeo8e53dnah" />
                <span data-id="element-169" data-mp="ew0m9eru88lj">{alert.text}</span>
              </li>)}
          </ul>
        </div>
      </div>

      <p className="border-t border-white/10 bg-graphite-900 px-4 py-3 text-[11px] leading-relaxed text-mist-300" data-id="element-170" data-mp="9xz2ssqi279j">
        Interfaz de referencia con datos de demostración. En producción muestra la información real de su operación.
      </p>
    </div>;
}